import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { getAccessTokenExpiryMs } from '../../utils/teleSession';

/** Show the stay-signed-in prompt this long before the token expires. */
const WARN_BEFORE_MS = 5 * 60 * 1000;

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

type SessionKeepAliveProps = {
  onLogout: () => void;
};

/**
 * Asks the signed-in user to stay before the token expires.
 * Stay calls the refresh endpoint. Silence logs them out. The session is not extended in the background.
 */
export default function SessionKeepAlive({ onLogout }: SessionKeepAliveProps) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let timer = 0;

    const tick = () => {
      window.clearTimeout(timer);
      const exp = getAccessTokenExpiryMs();
      if (exp == null) return;
      const left = exp - Date.now();
      setRemainingMs(left);
      if (left <= 0) {
        onLogout();
        return;
      }
      setOpen(left <= WARN_BEFORE_MS);
      const delay = left <= WARN_BEFORE_MS ? 1000 : Math.min(15_000, left - WARN_BEFORE_MS);
      timer = window.setTimeout(tick, Math.max(250, delay));
    };

    tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [onLogout]);

  const stay = async () => {
    setBusy(true);
    setError('');
    try {
      await apiService.refreshAccessToken();
      setOpen(false);
    } catch {
      setError('Could not extend the session. You can try again, or sign in again.');
    } finally {
      setBusy(false);
    }
  };

  if (!open || remainingMs == null || remainingMs <= 0) return null;

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="session-stay-title">
        <h2 id="session-stay-title" className="modal-title">
          Session expiring
        </h2>
        <p className="session-stay__body">
          You will be signed out in <strong>{formatRemaining(remainingMs)}</strong>. Stay signed in only if
          you are still at this screen. That renews the session. It cannot extend past 12 hours from
          when you signed in.
        </p>
        {error ? <p className="session-stay__error">{error}</p> : null}
        <div className="action-buttons">
          <button type="button" className="btn btn-secondary" onClick={onLogout} disabled={busy}>
            Log out
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void stay()} disabled={busy}>
            {busy ? 'Extending…' : 'Stay signed in'}
          </button>
        </div>
      </div>
    </div>
  );
}
